import { FC, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';

import If from 'components/Generics/If/If';
import { InfoCardView } from 'components/InfoCardView/InfoCardView';
import RhfRadioGroup from 'components/RadioGroup/RhfRadioGroup/RhfRadioGroup';
import { ZENCLEAR_LITE_ENABLED_COUNTRIES } from 'config';
import { TREATMENTPLANTYPEOPTIONS } from 'constants/options';
import {
  ZENCLEAR,
  ZENCLEAR_LITE,
  ZENCLEAR_ULTRA,
  ZENPLUS
} from 'types/TPVForm.types';
import { customTwMerge, translate } from 'utils';
import { useBracesTemplatizedOcclusionFields } from 'utils/customHooks/useBracesTemplatizedOcclusionFields';
import {
  filterZenClearUltraTreatmentPlanOptions,
  isZenClearUltraAvailable,
  mapOptionsWithSets
} from 'utils/TreatmentPlanningInstructions.utils';
import { getDefaultZenClearUltraTreatmentSpec } from 'utils/ultraPrescription.utils';

import TreatmentSpecificationsExternalUltra from './TreatmentSpecificationsExternalUltra';
import TreatmentSpecificationsExternalZenclear from './TreatmentSpecificationsExternalZenclear';
import TreatmentSpecificationsExternalZenplus from './TreatmentSpecificationsExternalZenPlus';

const EXTERNAL_OCCLUSION_BRANCHES = [
  'ZenClear',
  'ZenClearLite',
  'ZenPlus'
] as const;
type ExternalOcclusionBranch = (typeof EXTERNAL_OCCLUSION_BRANCHES)[number];

type Props = {
  isDisabled?: boolean;
  isReferral?: boolean;
  className?: string;
  onRadioButtonClick?: () => void;
  source?: string;
  clinicCountry?: string;
  doctorComplexity?: number;
};
const TreatmentSpecificationsExternal: FC<Props> = ({
  isDisabled,
  isReferral,
  className = '',
  onRadioButtonClick,
  source,
  clinicCountry,
  doctorComplexity
}) => {
  const { watch, setValue, getValues } = useFormContext();
  const watchTreatmentPlanType = watch('treatmentSpecifications.treatmentType');
  const applyBracesTemplatizedOcclusionFields =
    useBracesTemplatizedOcclusionFields();
  const isPrescriptionSummary = source === 'PrescriptionSummary';
  const isViewOnly = !!isDisabled || isPrescriptionSummary;
  // Ultra is braces-templatized only; summary/view-only may still show historical Ultra.
  const allowZenClearUltra =
    applyBracesTemplatizedOcclusionFields || isViewOnly;
  /** Last type we finished migrating for (updated only in `useEffect`). */
  const committedTreatmentTypeRef = useRef<string | undefined>(undefined);
  /**
   * Last known occlusion per branch. ZenPlus → ZenClear can run after ZenPlus
   * fields unregister on unmount, so `getValues` no longer sees Plus — we
   * merge live reads into this ref whenever they exist.
   */
  const occlusionSnapshotRef = useRef<
    Partial<
      Record<
        ExternalOcclusionBranch,
        { occlusion?: string | null; teeth?: unknown }
      >
    >
  >({});

  const externalPlanFormKey = (
    plan: string | undefined
  ): 'ZenClear' | 'ZenClearLite' | 'ZenPlus' | null => {
    if (plan === ZENCLEAR) return 'ZenClear';
    if (plan === ZENCLEAR_LITE) return 'ZenClearLite';
    if (plan === ZENPLUS) return 'ZenPlus';
    return null;
  };

  const mergeBranchIntoOcclusionSnapshot = useCallback(
    (branch: ExternalOcclusionBranch, branchData: Record<string, unknown>) => {
      const o = branchData.occlusionCorrection;
      const t = branchData.occlusionCorrectionTeeth;
      if (o !== undefined && o !== null) {
        occlusionSnapshotRef.current[branch] = {
          ...occlusionSnapshotRef.current[branch],
          occlusion: o as string
        };
      }
      if (t !== undefined && t !== null) {
        occlusionSnapshotRef.current[branch] = {
          ...occlusionSnapshotRef.current[branch],
          teeth: t
        };
      }
    },
    []
  );

  const refreshOcclusionSnapshotFromForm = useCallback(() => {
    EXTERNAL_OCCLUSION_BRANCHES.forEach((k) => {
      const o = getValues(`treatmentSpecifications.${k}.occlusionCorrection`);
      const t = getValues(
        `treatmentSpecifications.${k}.occlusionCorrectionTeeth`
      );
      if (o !== undefined && o !== null) {
        occlusionSnapshotRef.current[k] = {
          ...occlusionSnapshotRef.current[k],
          occlusion: o
        };
      }
      if (t !== undefined && t !== null) {
        occlusionSnapshotRef.current[k] = {
          ...occlusionSnapshotRef.current[k],
          teeth: t
        };
      }
    });
  }, [getValues]);

  const readOcclusionForMigration = useCallback(
    (branch: ExternalOcclusionBranch) => {
      const live = getValues(
        `treatmentSpecifications.${branch}.occlusionCorrection`
      );
      if (live !== undefined && live !== null) return live;
      const cached = occlusionSnapshotRef.current[branch]?.occlusion;
      if (cached !== undefined && cached !== null) return cached;
      return undefined;
    },
    [getValues]
  );

  const readOcclusionTeethForMigration = useCallback(
    (branch: ExternalOcclusionBranch) => {
      const live = getValues(
        `treatmentSpecifications.${branch}.occlusionCorrectionTeeth`
      );
      if (live !== undefined && live !== null) return live;
      const cached = occlusionSnapshotRef.current[branch]?.teeth;
      if (cached !== undefined && cached !== null) return cached;
      return undefined;
    },
    [getValues]
  );

  const copyOcclusionBetweenPlanBranches = useCallback(
    (
      prevPlan: string | undefined,
      currentPlan: string | undefined,
      formValuesRoot?: Record<string, unknown>
    ) => {
      const fromKey = externalPlanFormKey(prevPlan);
      const toKey = externalPlanFormKey(currentPlan);
      if (!fromKey || !toKey || fromKey === toKey) return;

      const ts = formValuesRoot?.treatmentSpecifications as
        | Record<string, unknown>
        | undefined;
      const fromBranch = ts?.[fromKey] as Record<string, unknown> | undefined;

      let priorOcclusion: string | undefined;
      let priorTeeth: unknown;

      if (fromBranch && typeof fromBranch === 'object') {
        const o = fromBranch.occlusionCorrection;
        const t = fromBranch.occlusionCorrectionTeeth;
        if (o !== undefined && o !== null) priorOcclusion = o as string;
        if (t !== undefined && t !== null) priorTeeth = t;
      }
      if (priorOcclusion === undefined) {
        priorOcclusion = readOcclusionForMigration(fromKey) as
          | string
          | undefined;
      }
      if (priorTeeth === undefined) {
        priorTeeth = readOcclusionTeethForMigration(fromKey);
      }

      if (priorOcclusion !== undefined && priorOcclusion !== null) {
        setValue(
          `treatmentSpecifications.${toKey}.occlusionCorrection`,
          priorOcclusion,
          { shouldDirty: true, shouldValidate: false }
        );
      }
      if (priorTeeth !== undefined && priorTeeth !== null) {
        setValue(
          `treatmentSpecifications.${toKey}.occlusionCorrectionTeeth`,
          priorTeeth,
          { shouldDirty: true, shouldValidate: false }
        );
      }
    },
    [readOcclusionForMigration, readOcclusionTeethForMigration, setValue]
  );

  /**
   * Sync occlusion snapshot from RHF (same tick as field updates). When plan
   * type changes, migrate using this `formValues` payload first — it often still
   * contains the previous branch (e.g. ZenPlus) before React unmount removes it
   * from `getValues`.
   */
  useEffect(() => {
    const shouldInspectFormValues = (name?: string) => {
      if (!name) return true;
      if (name === 'treatmentSpecifications.treatmentType') return true;
      return EXTERNAL_OCCLUSION_BRANCHES.some(
        (b) =>
          name === `treatmentSpecifications.${b}.occlusionCorrection` ||
          name === `treatmentSpecifications.${b}.occlusionCorrectionTeeth`
      );
    };

    const subscription = watch((formValues, { name }) => {
      if (!shouldInspectFormValues(name)) return;
      const root = formValues as Record<string, unknown>;
      const ts = root?.treatmentSpecifications as
        | Record<string, Record<string, unknown> | undefined>
        | undefined;
      if (!ts) return;

      EXTERNAL_OCCLUSION_BRANCHES.forEach((k) => {
        const branch = ts[k];
        if (!branch || typeof branch !== 'object') return;
        mergeBranchIntoOcclusionSnapshot(k, branch as Record<string, unknown>);
      });

      if (name === 'treatmentSpecifications.treatmentType') {
        const current = (ts as { treatmentType?: string }).treatmentType;
        const prev = committedTreatmentTypeRef.current;
        copyOcclusionBetweenPlanBranches(prev, current, root);
      }
    });
    return () => subscription.unsubscribe();
  }, [
    watch,
    mergeBranchIntoOcclusionSnapshot,
    copyOcclusionBetweenPlanBranches
  ]);

  /**
   * ZenClear, ZenClear Lite, and ZenPlus each store occlusion under different
   * `treatmentSpecifications.*` paths. When the user changes plan type before
   * submit, copy occlusion correction + teeth from the previously selected
   * branch onto the new one (including ZenPlus ↔ ZenClear, Clear ↔ Lite, and
   * reverse) so values are not lost on remount.
   *
   * `useLayoutEffect`: runs before child `useEffect` so ZenPlus → ZenClear does
   * not lose the value to `OcclusionCorrectionInput` normalising undefined to
   * None on first mount of the new branch.
   *
   * `useEffect` (second pass): ZenClear ↔ ZenClear Lite reuses the same
   * subtree and only changes `formType`; react-hook-form may re-register the
   * new field name in a child `useEffect` with Controller `defaultValue`, which
   * runs *after* this layout copy and can reset occlusion to None. Re-applying
   * the migration after child effects restores Posterior / teeth selection.
   */
  useLayoutEffect(() => {
    refreshOcclusionSnapshotFromForm();
    const current = watchTreatmentPlanType;
    const prev = committedTreatmentTypeRef.current;
    copyOcclusionBetweenPlanBranches(prev, current);
  }, [
    watchTreatmentPlanType,
    refreshOcclusionSnapshotFromForm,
    copyOcclusionBetweenPlanBranches
  ]);

  useEffect(() => {
    refreshOcclusionSnapshotFromForm();
    const current = watchTreatmentPlanType;
    const prev = committedTreatmentTypeRef.current;
    copyOcclusionBetweenPlanBranches(prev, current);
    committedTreatmentTypeRef.current = current;
  }, [
    watchTreatmentPlanType,
    refreshOcclusionSnapshotFromForm,
    copyOcclusionBetweenPlanBranches
  ]);

  const isLiteCountry = ZENCLEAR_LITE_ENABLED_COUNTRIES.includes(
    clinicCountry || ''
  );
  const filteredOptions = isLiteCountry
    ? TREATMENTPLANTYPEOPTIONS
    : TREATMENTPLANTYPEOPTIONS.filter(
        (option) => option.value !== ZENCLEAR_LITE
      );
  const filteredTreatmentPlanTypeOptions = mapOptionsWithSets(
    filterZenClearUltraTreatmentPlanOptions(
      filteredOptions,
      doctorComplexity,
      allowZenClearUltra
    ),
    isLiteCountry
  );

  useEffect(() => {
    if (isViewOnly) return;

    const ultraDisallowed =
      !allowZenClearUltra || !isZenClearUltraAvailable(doctorComplexity);
    if (ultraDisallowed && watchTreatmentPlanType === ZENCLEAR_ULTRA) {
      setValue('treatmentSpecifications.treatmentType', '', {
        shouldDirty: true
      });
      setValue(
        'treatmentSpecifications.ZenClearUltra',
        getDefaultZenClearUltraTreatmentSpec()
      );
    }
  }, [
    isViewOnly,
    allowZenClearUltra,
    doctorComplexity,
    watchTreatmentPlanType,
    setValue
  ]);

  return (
    <div className={customTwMerge('flex flex-col space-y-5', className)}>
      <If condition={!!watchTreatmentPlanType && !isReferral}>
        <InfoCardView
          message={translate('treatmentSpecification.external.info.message')}
          classname='py-3.5 px-4 md:items-center'
          isExternal={false}
          iconClassname='text-YELLOW_4 mr-2'
        >
          <ul className='list-disc'>
            <li>{translate('aligner.info.prescription')}</li>
            <li>{translate('teeth.position.warning.info')}</li>
          </ul>
        </InfoCardView>
      </If>
      <div
        className={classNames('flex flex-col p-18px rounded-md space-y-8', {
          'bg-GRAY5': !isReferral
        })}
      >
        <RhfRadioGroup
          options={filteredTreatmentPlanTypeOptions}
          title={translate('prescriptions.treatment_plan_type')}
          className='mt-3 flex flex-col mb-0'
          name='treatmentSpecifications.treatmentType'
          radioClassName='border-RADIO_BORDER'
          radioItemFont='md:text-17px text-sm'
          inputProps={{ disabled: isDisabled }}
          onClick={onRadioButtonClick}
        />
        <If condition={!!watchTreatmentPlanType}>
          <If
            condition={
              watchTreatmentPlanType === ZENCLEAR ||
              watchTreatmentPlanType === ZENCLEAR_LITE
            }
          >
            <TreatmentSpecificationsExternalZenclear
              formType={
                watchTreatmentPlanType === ZENCLEAR_LITE
                  ? 'ZenClearLite'
                  : 'ZenClear'
              }
              isDisabled={isDisabled}
              isReferral={isReferral}
              source={source}
            />
          </If>
          <If condition={watchTreatmentPlanType === ZENPLUS}>
            <TreatmentSpecificationsExternalZenplus
              formType={'ZenPlus'}
              isDisabled={isDisabled}
              isReferral={isReferral}
              source={source}
            />
          </If>
          <If condition={watchTreatmentPlanType === ZENCLEAR_ULTRA}>
            <TreatmentSpecificationsExternalUltra isDisabled={isDisabled} />
          </If>
        </If>
      </div>
    </div>
  );
};

export default TreatmentSpecificationsExternal;
